import { FileText } from 'lucide-react';
import { Link } from 'react-router-dom';

const EFFECTIVE = '25 June 2026';
const CONTACT_EMAIL = 'legal@transitdeal.co.uk';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="text-lg font-bold text-gray-900 mb-3 pb-2 border-b border-gray-100">{title}</h2>
      <div className="space-y-3 text-sm text-gray-700 leading-relaxed">{children}</div>
    </section>
  );
}

function Sub({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-800 mb-1.5">{title}</h3>
      <div className="space-y-2 text-sm text-gray-600 leading-relaxed">{children}</div>
    </div>
  );
}

function Ul({ items }: { items: string[] }) {
  return (
    <ul className="list-disc list-inside space-y-1 text-gray-600 ml-2">
      {items.map((item, idx) => <li key={idx}>{item}</li>)}
    </ul>
  );
}

export default function TermsPage() {
  return (
    <div className="page-shell-prose">
      {/* Header */}
      <div className="flex items-start gap-4 mb-8">
        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center shrink-0 mt-1">
          <FileText className="w-5 h-5 text-indigo-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Terms &amp; Conditions</h1>
          <p className="text-sm text-gray-500 mt-1">
            Effective date: {EFFECTIVE} &nbsp;·&nbsp; TransitDeal Ltd &nbsp;·&nbsp; Version 2.0
          </p>
        </div>
      </div>

      <div className="card p-6 sm:p-8">

        <p className="text-sm text-gray-700 leading-relaxed mb-8 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          <strong>Please read these Terms carefully.</strong> By creating an account or using the TransitDeal
          platform, you agree to be bound by these Terms. If you do not agree, you must not use the platform.
          These Terms apply in addition to our <a href="/policies/website-usage-policy.html" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Website &amp; App Usage Policy</a>,
          our <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>, and our{' '}
          <Link to="/cookies" className="text-blue-600 hover:underline">Cookie Policy</Link>. Where there is a
          conflict on a contractual matter (payment, fees, job liability), these Terms govern.
        </p>

        <Section title="1. About TransitDeal">
          <p>
            TransitDeal Ltd (<strong>"TransitDeal"</strong>, <strong>"we"</strong>, <strong>"us"</strong>, or <strong>"our"</strong>)
            is a company registered in England and Wales (Company No. [COMPANY_REG_NO]), registered office at
            [REGISTERED_ADDRESS]. We operate an online marketplace (the <strong>"Platform"</strong>) that connects
            customers (shippers) with drivers, hauliers, fleet carriers, and workshops for the purpose of
            arranging and completing transport, delivery, relay, removal, freight haulage, and vehicle repair
            and servicing within the United Kingdom.
          </p>
          <p>
            TransitDeal is a <strong>technology platform and marketplace</strong>. We do not ourselves carry
            goods, provide driving services, or carry out vehicle repairs. All transport and repair services
            are provided by independent third-party service providers ("Service Providers") who use the Platform.
          </p>
        </Section>

        <Section title="2. Definitions">
          <Ul items={[
            '"Job" means a transport, delivery, relay, removal, freight haulage, or vehicle repair/servicing task posted on the Platform by a Customer.',
            '"Quote" means a binding offer submitted by a Service Provider to carry out a Job at a stated price.',
            '"Service Provider" means a Carrier or a Workshop, as the context requires.',
            '"Carrier" means a driver, haulier, or fleet carrier who quotes on and performs Delivery Jobs.',
            '"Workshop" means a garage, mechanic, or repair business that quotes on and performs Repair Jobs.',
            '"Delivery Job" means a Same-Day, Scheduled, Relay, Removal, Freight Haulage, Multi-Drop, or Multi-Pickup-and-Drop Job.',
            '"Repair Job" means a vehicle repair or servicing Job, priced as a labour element and a parts element.',
            '"Platform Fee" means the fee TransitDeal charges on a completed Job, as described in Section 7.',
            '"POD" means proof of delivery, captured as described in Section 8.',
            '"Contract" means the legally binding agreement formed directly between a Customer and a Service Provider on acceptance of a Quote.',
          ]} />
        </Section>

        <Section title="3. Eligibility">
          <Ul items={[
            'You must be at least 18 years old to use TransitDeal.',
            'You must be legally entitled to work or trade in the United Kingdom.',
            'Carriers must hold a valid, full UK driving licence appropriate for their vehicle class.',
            'Carriers must maintain valid Hire & Reward (H&R) insurance at all times while actively accepting Jobs on the Platform.',
            'By accepting these Terms you confirm that all information you provide is accurate, complete, and up to date.',
          ]} />
        </Section>

        <Section title="4. Account Registration, Verification & Vetting">
          <Sub title="4.1 Creating an Account">
            <p>
              You register using your email address and password via our identity provider (Microsoft Azure
              our authentication service). You are responsible for keeping your credentials secure. You must notify us
              immediately at <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-600 hover:underline">{CONTACT_EMAIL}</a> if
              you suspect unauthorised access to your account.
            </p>
          </Sub>
          <Sub title="4.2 Profile Completion & Verification">
            <p>
              You may complete your profile in stages. However, certain features (such as accepting paid Jobs
              or posting Jobs) require a verified, complete profile. TransitDeal reserves the right to restrict
              access to features until mandatory verification steps are complete.
            </p>
          </Sub>
          <Sub title="4.3 One Account Per Person or Entity">
            <p>
              Each individual or legal entity may hold one active account. Creating multiple accounts to
              circumvent suspensions, restrictions, vetting requirements, or referral-eligibility rules is a
              material breach of these Terms.
            </p>
          </Sub>
          <Sub title="4.4 Carrier & Workshop Vetting Pathways">
            <p>
              Before a Carrier or Workshop may submit a Quote or accept a Job, they must satisfy one of the
              following three vetting pathways:
            </p>
            <Ul items={[
              'Pathway A — External Reviews: a minimum number of verified ratings/reviews from another recognised marketplace (e.g. Google, Trustpilot, Checkatrade), submitted with proof, and verified by our operations team.',
              'Pathway B — Referral: referred by an existing Carrier or Workshop who has themselves completed at least one Job successfully on the Platform.',
              'Pathway C — Refundable Security Deposit: payment of a refundable security deposit, held by TransitDeal and returned automatically once the Carrier or Workshop has completed the qualifying number of verified Jobs at or above the minimum average rating, both as stated on the Platform at the time.',
            ]} />
            <p>The applicable deposit amount, review threshold, and qualifying job count are displayed on the Platform during onboarding and may be updated from time to time.</p>
          </Sub>
          <Sub title="4.5 Identity & Compliance Documents">
            <p>
              Carriers must provide and keep current: a valid UK driving licence (verified either via a DVLA
              share code reviewed by our operations team, or via our AI-assisted licence scan feature, in each
              case subject to manual confirmation), a current MOT certificate where applicable, Hire &amp;
              Reward insurance, Goods in Transit insurance, and Public Liability insurance (minimum £1,000,000
              cover). No Carrier may submit a Quote or accept a Job until these documents are verified. We
              monitor expiry dates and send automated reminders; accounts are automatically suspended if a
              mandatory document lapses.
            </p>
          </Sub>
        </Section>

        <Section title="5. User Roles and Responsibilities">
          <Sub title="5.1 Customers (Shippers)">
            <p>Customers may post Jobs describing transport or vehicle service requirements. By posting a Job, you confirm that:</p>
            <Ul items={[
              'You have the right to offer the goods for transport or the vehicle for service.',
              'The goods described are accurate and legal to transport under UK law.',
              'You will make full payment within the agreed terms once the Job is completed.',
              'You will be available at pickup/delivery points at the agreed times, or have authorised a suitable representative.',
            ]} />
          </Sub>

          <Sub title="5.2 Carriers (Drivers, Hauliers & Fleet Carriers)">
            <p>Carriers may submit Quotes and accept Jobs. By accepting a Job, you confirm that you:</p>
            <Ul items={[
              'Hold a valid UK driving licence appropriate for the vehicle and load.',
              'Hold valid Hire & Reward (H&R) insurance for the journey.',
              'Hold valid Goods in Transit (GIT) insurance where required by the Job.',
              'Hold valid Public Liability Insurance (PLI).',
              'That your vehicle has a current, valid MOT certificate.',
              'Comply with driver hours regulations (EU/UK AETR and GB domestic rules as applicable).',
              'Will complete the Job to the standard of a reasonably competent professional driver.',
              "Will not subcontract or assign Jobs without TransitDeal's prior written consent.",
            ]} />
            <p>
              Fleet Carriers may register multiple vehicles and onboard employees via the Employee Portal
              (Section 11). A Fleet Carrier remains responsible for the conduct of any employee who accesses
              the Platform through a magic-link it has issued, and may revoke that access at any time.
            </p>
          </Sub>

          <Sub title="5.3 Workshops">
            <p>
              Workshops may quote on vehicle repair and servicing Jobs. Workshops warrant that they employ or
              engage qualified mechanics and that all work is carried out to an appropriate professional
              standard. Workshops may onboard mechanic employees in the same way as a Fleet Carrier onboards
              drivers (Section 11), and remain responsible for their conduct.
            </p>
          </Sub>

          <Sub title="5.4 Insurance Entities">
            <p>
              Users registered as Insurance Entities may quote on insurance-related Jobs only and do not see
              Delivery or Repair Job categories. Bidding visibility is enforced at the platform level according
              to the verified entity category assigned at registration.
            </p>
          </Sub>

          <Sub title="5.5 Independent Status">
            <p>
              Service Providers (Carriers, Workshops) are <strong>independent contractors</strong>, not
              employees, workers, or agents of TransitDeal. Nothing in these Terms creates a partnership,
              employment relationship, or agency between TransitDeal and any Service Provider.
            </p>
          </Sub>
        </Section>

        <Section title="6. Jobs, Quotes and Contract Formation">
          <Sub title="6.1 Posting a Job">
            <p>
              Jobs posted must accurately describe the nature of the goods or repair required, the pickup and
              delivery locations (or vehicle location, for Repair Jobs), and any special handling requirements.
              Misleading or fraudulent Job descriptions are a breach of these Terms and may result in account
              suspension. Jobs are screened by an automated review process for quality and policy issues; this
              screening is an operational aid and does not constitute a warranty that any Job is genuine,
              accurate, or lawful.
            </p>
            <p>
              The Platform supports several Job types: Same-Day Delivery, Scheduled Delivery, Relay Delivery,
              Removal, Freight Haulage, Multi-Drop Delivery (sequential drops from one pickup), Multi-Pickup
              and Multi-Drop Delivery (multiple pickups feeding multiple drops), and Vehicle Repair/Servicing.
            </p>
          </Sub>
          <Sub title="6.2 Quotes">
            <p>
              Quotes submitted by Service Providers are binding offers. For Repair Jobs, a Quote separately
              states a labour element and a parts element. Once a Customer accepts a Quote, a binding Contract
              is formed directly between the Customer and the Service Provider. TransitDeal is not a party to
              that Contract.
            </p>
          </Sub>
          <Sub title="6.3 Auto-Generated Contracts on Bid Award">
            <p>
              When a Quote is accepted, TransitDeal automatically generates a Contract document from a
              pre-approved template. It covers the Job scope, the agreed price, the Platform Fee, full upfront
              payment confirmation, the cancellation policy, no-show terms, the POD verification requirement,
              credit-terms obligations (where applicable), and the governing law. The Contract is made available
              to both parties on the Platform and, where applicable, by email.
            </p>
          </Sub>
          <Sub title="6.4 Relay Delivery Jobs">
            <p>
              A Relay Job may be broken into geographic segments, each carried out by a different Carrier, with
              automatic handover instructions between segments. A distance-tiered relay coordination fee
              applies in addition to the Platform Fee; the applicable rate is displayed before you submit or
              accept a Quote and may be updated from time to time.
            </p>
          </Sub>
          <Sub title="6.5 Multi-Stop, Multi-Pickup & Multi-Drop Jobs">
            <p>
              For Multi-Drop and Multi-Pickup-and-Drop Jobs, the Carrier must complete each pickup and/or drop
              in sequence, capturing location data, photographic evidence, a signature, and the relevant
              contact name at each stop. A failed pickup or drop must be recorded with a reason. These Job
              types are charged the standard Delivery Platform Fee on the total Job value.
            </p>
          </Sub>
          <Sub title="6.6 Acceptance and Cancellation">
            <p>
              Once a Quote is accepted, cancellation by either party may incur a fee as set out in our
              Cancellation Policy (Section 7.6) and available on the Platform. TransitDeal reserves the right
              to remove Jobs that violate these Terms without compensation to the posting Customer.
            </p>
          </Sub>
        </Section>

        <Section title="7. Payments, Platform Fee and Escrow">
          <Sub title="7.1 Stripe Connect Escrow & Full Upfront Payment">
            <p>
              All payments on the Platform are processed by Stripe Payments Europe, Ltd ("Stripe") via Stripe
              Connect. You agree to Stripe's terms of service and privacy policy. Customers pay the full agreed
              Job value at the point of accepting a Quote; funds are held in escrow by Stripe and released to
              the Service Provider once Job completion has been confirmed under Section 8. TransitDeal does not
              hold customer or Service Provider funds in any internal wallet.
            </p>
          </Sub>
          <Sub title="7.2 The Platform Fee">
            <p>
              TransitDeal charges a Platform Fee — a commission on completed Jobs — which is deducted
              automatically from the funds held in escrow before the Service Provider is paid:
            </p>
            <Ul items={[
              'For Delivery Jobs, the Platform Fee is calculated on the full agreed Job value.',
              'For Repair Jobs, the Platform Fee is calculated on the labour element of the agreed price only. The parts element is excluded from the Platform Fee and passes through to the Customer and Workshop with no platform margin.',
            ]} />
            <p>
              The applicable Platform Fee is always displayed to you before you submit or accept a Quote, and
              is confirmed again in the Contract generated on bid award. TransitDeal may vary the Platform Fee
              from time to time; any change will be reflected in what is displayed to you at the point of
              quoting and will not affect a Job already accepted.
            </p>
          </Sub>
          <Sub title="7.3 Credit Terms & Identity Verification">
            <p>
              Where a Customer requests a Job on credit terms and the Service Provider accepts, the Service
              Provider is liable to pay TransitDeal's Platform Fee immediately on Job completion, rather than
              having it deducted from an upfront payment. Before credit terms are extended, the Customer must
              pass an identity verification check: individuals must submit a government-issued identity
              document; businesses must submit company registration details and the name of a director. Our
              operations team reviews and approves each credit request before the Job may proceed on credit
              terms. Non-payment of the Platform Fee within the required window results in account suspension.
            </p>
          </Sub>
          <Sub title="7.4 Payment Release & Reconciliation">
            <p>
              Release of escrowed funds to a Service Provider follows an internal two-step review: an
              operations reviewer first approves the payment, and a separate administrative step performs the
              release. This is an internal control and does not affect your rights under these Terms.
            </p>
          </Sub>
          <Sub title="7.5 VAT">
            <p>
              Where applicable, VAT will be charged in addition to Job prices in accordance with UK VAT
              legislation. Business accounts with valid VAT numbers may be eligible for reverse-charge or
              zero-rated treatment as applicable.
            </p>
          </Sub>
          <Sub title="7.6 Cancellation Fees & Refunds">
            <p>
              Where a Customer cancels a Job within the cancellation window stated on the Platform at the time
              of booking, a late-cancellation fee (calculated as a proportion of the Job value and displayed to
              you before you cancel) may be payable to the Service Provider, with the remainder refunded to the
              Customer. Cancellation outside that window is refunded in full. Cancellation reasons are recorded
              (for example: fraud suspected, customer changed their mind, vehicle breakdown, carrier
              unavailable, no-show, duplicate posting, pricing dispute, or other) and determine the applicable
              outcome. Where the Service Provider cancels or fails to honour an accepted Contract, the Customer
              receives a full refund and the Service Provider may incur a rating penalty and account review.
            </p>
          </Sub>
          <Sub title="7.7 Cashback & Referral Incentives">
            <p>
              From time to time, TransitDeal may offer a cashback incentive on the value of qualifying completed
              Jobs, and a referral incentive on Jobs completed by a user you have referred. Both incentives are
              calculated as a proportion of Job value; the applicable rate, qualifying period, and eligibility
              conditions (including that a referral code is unlocked only after your own first successfully
              completed Job, and that companies may refer only companies while individuals may refer only
              individuals) are displayed on the Platform and may change from time to time. These incentives are
              discretionary and may be withdrawn or amended by TransitDeal at any time, subject to honouring
              incentives already earned on completed Jobs.
            </p>
          </Sub>
          <Sub title="7.8 Disputes About Payments">
            <p>
              Payment disputes must be raised through the TransitDeal disputes system within <strong>7 days</strong> of
              the Job completion date. We will mediate disputes in good faith but are not liable for the
              outcome of disputes between Customers and Service Providers.
            </p>
          </Sub>
        </Section>

        <Section title="8. Proof of Delivery, GPS and IP Verification">
          <p>
            For Delivery Jobs, the Carrier must submit proof of delivery (POD) at the point of completion. This
            captures: the device's GPS coordinates at the moment of submission, validated against the delivery
            address; the device's public IP address, logged server-side; one or more photographs; and, where
            applicable, the recipient's signature and name. A mismatch between the GPS location and the
            delivery address, or an IP address flagged as suspicious (for example, use of a VPN/proxy, or a
            non-UK location), will place the payment release on hold pending review by our operations team. For
            Repair Jobs, equivalent completion evidence is required as set out on the Platform. POD records are
            stored immutably and may be relied upon by either party in the event of a dispute.
          </p>
        </Section>

        <Section title="9. Ratings, Reviews and External Rating Porting">
          <p>
            After a Job is completed, both parties may leave star ratings and written reviews. Reviews must be
            honest, based on your genuine experience, and must not contain defamatory, discriminatory, or false
            statements. TransitDeal reserves the right to remove reviews that breach these requirements. A
            rated party may post a single public response to any review. Carriers and Workshops whose average
            rating falls below the minimum threshold stated on the Platform may be flagged for review or
            suspended pending appeal. As described in Section 4.4, new Carriers and Workshops may also import
            verified ratings from another recognised platform as part of onboarding, subject to our operations
            team verifying their authenticity.
          </p>
        </Section>

        <Section title="10. AI-Assisted Features">
          <p>
            The Platform includes AI-assisted features: a text and voice chatbot that can help with
            registration, Job posting, and Quote submission; an automated Job-review process that screens
            posted Jobs for quality and policy issues; and AI-assisted document scanning (for example, extracting
            details from a photographed driving licence). These features assist you but do not replace your own
            responsibility for the accuracy of what you submit, and do not constitute a warranty that any Job,
            user, or document is genuine, accurate, or lawful. You must not use these features to generate or
            submit fraudulent or misleading content, or to attempt to extract or manipulate the underlying AI
            models or prompts.
          </p>
        </Section>

        <Section title="11. Fleet Management & Employee Portal">
          <p>
            Fleet Carriers and Workshops may register multiple vehicles or employees and issue a magic-link
            token granting an employee scoped access to an Employee Portal, without that employee holding a
            separate full account. Through the Employee Portal an employee may view Jobs owned by or assigned
            to their employer, update location while a Job is active, start and complete Jobs, and upload
            delivery or repair evidence. The Fleet Carrier or Workshop remains responsible for the conduct of
            any employee using a magic link it has issued, and may revoke that access at any time.
          </p>
        </Section>

        <Section title="12. Prohibited Conduct">
          <p>You must not use the Platform to:</p>
          <Ul items={[
            'Transport illegal goods (weapons, controlled drugs, counterfeit goods, hazardous materials without appropriate permits)',
            'Post or accept fictitious Jobs, or collude on pricing',
            'Impersonate another person or entity, or provide false identity, licence, or insurance information',
            'Circumvent our payment system by arranging off-platform payment for a Job introduced through TransitDeal',
            'Harvest or scrape data from the Platform without our written permission',
            'Introduce malware, viruses, or any other harmful code',
            'Discriminate against users on the basis of protected characteristics under the Equality Act 2010',
            'Harass, abuse, or threaten other users',
            'Use the AI chatbot or voice assistant to generate fraudulent or misleading content',
            'Falsify or spoof GPS location or IP address data in connection with proof of delivery',
            'Create multiple accounts to evade a suspension, vetting requirement, or referral-eligibility rule',
          ]} />
          <p>Breach of these prohibitions may result in immediate account suspension and, where appropriate, referral to law enforcement.</p>
        </Section>

        <Section title="13. TransitDeal's Role, Liability, and Warranties">
          <Sub title="13.1 Platform Provider Only">
            <p>
              TransitDeal provides a technology platform. We do not control, supervise, or inspect the services
              delivered by Service Providers. We make no warranty that Service Providers are licensed,
              competent, or insured beyond what is visible in their verified profile.
            </p>
          </Sub>
          <Sub title="13.2 Limitation of Liability">
            <p>
              To the maximum extent permitted by law, TransitDeal's total liability to you for any single
              claim arising out of or in connection with the Platform or these Terms shall not exceed the
              greater of: (a) the Platform Fee earned by us on the relevant Job; or (b) £500.
            </p>
            <p>We are not liable for:</p>
            <Ul items={[
              "Loss, damage, or delay to goods in transit (this is covered by the Service Provider's GIT insurance)",
              'Any indirect, consequential, or economic loss',
              'Failure to match Jobs with Service Providers',
              'Delays caused by third parties (including payment processors or cloud providers)',
              'Loss of data caused by user error',
            ]} />
          </Sub>
          <Sub title="13.3 Consumer Rights">
            <p>
              Nothing in these Terms excludes or limits liability for death or personal injury caused by our
              negligence, fraud or fraudulent misrepresentation, or any other liability that cannot be excluded
              under applicable UK law, including the Consumer Rights Act 2015 where you are a consumer.
            </p>
          </Sub>
        </Section>

        <Section title="14. Intellectual Property">
          <p>
            The TransitDeal name, logo, platform design, and all content created by us (including AI-generated
            platform content) are the exclusive property of TransitDeal Ltd and are protected by UK and
            international intellectual property laws. You may not reproduce, distribute, or create derivative
            works without our express written consent.
          </p>
          <p>
            By posting content on the Platform (Job descriptions, photos, reviews), you grant TransitDeal a
            royalty-free, non-exclusive, worldwide licence to use, store, and display that content for the
            purpose of operating the Platform.
          </p>
        </Section>

        <Section title="15. Suspension and Termination">
          <Sub title="15.1 By You">
            <p>You may close your account at any time by contacting us at {CONTACT_EMAIL}. Outstanding obligations (unpaid invoices, incomplete Jobs) must be resolved before closure.</p>
          </Sub>
          <Sub title="15.2 By TransitDeal">
            <p>We may suspend or terminate your account, with or without notice, if you:</p>
            <Ul items={[
              'Breach these Terms',
              'Provide false or misleading information',
              'Receive a pattern of negative ratings that indicates unsatisfactory service',
              'Fail to maintain the required insurance, licence, or compliance-document standards',
              'Fail to pay a Platform Fee due on credit terms within the required window',
              'Are subject to a regulatory investigation that affects your right to operate',
            ]} />
          </Sub>
          <Sub title="15.3 Effect of Termination">
            <p>On termination, your right to access the Platform ceases immediately. We will retain your data for the periods described in our Privacy Policy and as required by law.</p>
          </Sub>
        </Section>

        <Section title="16. Dispute Resolution">
          <p>
            We encourage you to contact us in the first instance at <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-600 hover:underline">{CONTACT_EMAIL}</a>.
            Most disputes can be resolved quickly through our in-platform disputes system. A non-refundable
            filing fee applies to opening a formal dispute case, the amount of which is displayed to you when
            you open a case; this may be recoverable from the opposing party where your case succeeds, at our
            operations team's discretion.
          </p>
          <p>
            If a dispute cannot be resolved informally, either party may refer it to mediation through a
            mutually agreed mediator. If mediation fails, disputes will be subject to the exclusive
            jurisdiction of the courts of England and Wales.
          </p>
          <p>
            If you are a consumer, you may also use the EU Online Dispute Resolution platform at{' '}
            <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">ec.europa.eu/consumers/odr</a>.
          </p>
        </Section>

        <Section title="17. Changes to These Terms">
          <p>
            We may update these Terms from time to time. We will notify you by email and/or an in-platform
            notice at least <strong>14 days</strong> before material changes take effect. Your continued use of
            the Platform after the effective date constitutes acceptance of the updated Terms. If you do not
            accept the changes, you must stop using the Platform and may close your account.
          </p>
        </Section>

        <Section title="18. Governing Law">
          <p>
            These Terms and any dispute or claim arising out of or in connection with them (including
            non-contractual disputes) shall be governed by and construed in accordance with the law of
            <strong> England and Wales</strong>. Subject to clause 16 (Dispute Resolution), you and we both
            submit to the exclusive jurisdiction of the courts of England and Wales.
          </p>
        </Section>

        <Section title="19. Contact">
          <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
            <p className="font-semibold text-gray-900">TransitDeal Ltd — Legal Team</p>
            <p className="text-gray-600">Email: <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-600 hover:underline">{CONTACT_EMAIL}</a></p>
            <p className="text-gray-600">Address: [REGISTERED_ADDRESS]</p>
          </div>
          <p className="mt-3">
            For privacy enquiries, see our <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>.
            For rules on acceptable use of the website and app, see our{' '}
            <a href="/policies/website-usage-policy.html" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Website &amp; App Usage Policy</a>.
          </p>
        </Section>
      </div>
    </div>
  );
}
